/**
 * Shielding into the Solana pool from the user's own wallet.
 *
 * The wallet is public in a deposit (it pays), exactly as on Base. What must
 * never leave the browser is the note. The same isolated WASM worker used
 * on Base proves locally; its result is checked against the note and Solana
 * token domain before simulation. There is no remote prover fallback.
 * The journal records the note
 * before the wallet is asked to sign, so a deposit that lands during a crash
 * is recovered from the commitment record rather than lost.
 */
import {
  SOLANA_TOKEN_PROGRAM_ID,
  buildSolanaDeposit,
  buildSolanaPublishRecoveryRecordInstruction,
  SOLANA_PACKET_DATA_SIZE,
  type SolanaInstruction,
  compileSolanaMessage,
  computeSolanaTokenHash,
  computeSolanaRecoveryRecordsDigest,
  type SolanaRecoveryRecordInput,
  decodeSolanaTokenConfig,
  getSolanaAccounts,
  simulateSolanaMessage,
  solanaComputeUnitLimitInstruction,
  solanaComputeUnitPriceInstruction,
  solanaPoolAddresses,
  solanaTransactionBytes,
  assertSolanaSimulationSucceeded,
  SOLANA_PLACEHOLDER_BLOCKHASH,
  type SolanaCompiledMessage,
  type SolanaDepositProofResponse,
  type SolanaPoolConfig,
  type SolanaTokenConfig,
} from "@stealthos/sdk";
import nacl from "tweetnacl";
import { solanaRecipientInboxKey } from "./recipient";
import { ethers } from "ethers";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../../joinsplit/types";
import {
  assertSolanaRegisteredToken,
  type SolanaNetworkConfig,
} from "../../config/solana";
import { proveDepositLocally } from "../../deposit-wasm/prover";
import { assertSolanaDepositProvingSetup } from "../../deposit-wasm/deployment";
import { flatFieldHex } from "../notes";
import { assertNotAborted, bytesEqual, fail } from "./common";
import { finalizeSolanaDeposit } from "./finalize";
import {
  activeSolanaJournalRecords,
  prepareSolanaJournalRecord,
  solanaDepositJournalId,
  solanaSettlementJournalId,
  updateSolanaJournalRecord,
  type SolanaJournalScope,
  type SolanaJournalRecord,
} from "./journal";
import { createSolanaFlatNote, solanaNoteMint } from "./notes";
import {
  finalizeSolanaDepositMerge,
  prepareSolanaDepositMerge,
  type SolanaDepositMergePrepared,
} from "./depositMerge";
import {
  confirmSolanaSignature,
  getSolanaBalance,
  getSolanaLatestBlockhash,
  getSolanaTokenBalance,
  sendSolanaTransaction,
  SolanaTransactionExpiredError,
  SolanaTransactionFailedError,
  type SolanaRpc,
} from "./rpc";
import { readSolanaPoolConfig } from "./sync";
import type { SolanaLeafCache } from "./leafCache";
import { formatInboxPublicKeyHex } from "../../utils/inbox-crypto";
import {
  prepareSolanaNoteRecovery,
  type SolanaRecoveryPublication,
} from "./onchainRecovery";
import {
  estimateSolanaDepositFees,
  SOLANA_DEPOSIT_MAX_COMPUTE_UNITS as MAX_COMPUTE_UNITS,
  SOLANA_DEPOSIT_COMPUTE_UNIT_PRICE as COMPUTE_UNIT_PRICE_MICRO_LAMPORTS,
} from "./depositFees";

const SIMULATION_HEADROOM_BPS = 11_500n;

export type SolanaDepositLog = (
  message: string,
  type?: "info" | "success" | "error" | "warning",
  signature?: string,
) => void;

/** The signer the app hands in: the connected wallet's address and signing feature. */
export interface SolanaDepositSigner {
  address: string;
  publicKey: Uint8Array;
  signTransaction(transaction: Uint8Array): Promise<Uint8Array>;
}

export interface SolanaDepositParams {
  rpc: SolanaRpc;
  network: SolanaNetworkConfig;
  signer: SolanaDepositSigner;
  spendingKeys: SpendingKeys;
  /** Fresh note candidates from completed recovery; [] only for an empty wallet. */
  notes: StoredJoinSplitNote[];
  /** Reuse public leaves already authenticated during recovery. */
  cache?: SolanaLeafCache;
  /** Defaults to the connected account. */
  recipientMpk?: bigint;
  recipientInboxPublicKeyHex?: string;
  mint: string | null;
  amount: bigint;
  journalScope: SolanaJournalScope;
  /** Must durably acknowledge the encrypted journal before simulation or signing. */
  backupJournal?: (record: SolanaJournalRecord) => Promise<void>;
  privateHistory?: JoinSplitNote["privateHistory"];
  onLog: SolanaDepositLog;
  signal?: AbortSignal;
  /** Test seam for confirmation pacing. */
  pollMs?: number;
}

export interface SolanaDepositResult {
  signature: string;
  slot: number;
  note: JoinSplitNote;
  journalId: string;
  /** Present when this deposit atomically replaced an existing private note. */
  spentNote?: JoinSplitNote;
}

/** Refuse a pool this build is not allowed to serve (mirrors the relayer). */
export function assertSolanaPoolServable(
  network: Pick<
    SolanaNetworkConfig,
    "allowDevelopmentSetup" | "cluster" | "label"
  >,
  poolConfig: Pick<
    SolanaPoolConfig,
    "paused" | "developmentSetup" | "unsafeTestSetupKeys"
  >,
): void {
  if (poolConfig.paused) fail("the pool is paused");
  if (
    network.cluster === "mainnet-beta" &&
    (poolConfig.developmentSetup || poolConfig.unsafeTestSetupKeys)
  ) {
    fail(
      "a pool with development or throwaway verifying keys is never served on mainnet-beta",
    );
  }
  if (
    (poolConfig.developmentSetup || poolConfig.unsafeTestSetupKeys) &&
    !network.allowDevelopmentSetup
  ) {
    fail(
      `${network.label} pool runs development verifying keys; set REACT_APP_SOLANA_DEVNET_ALLOW_DEVELOPMENT_SETUP=true to use it`,
    );
  }
}

async function readSolanaTokenConfig(
  rpc: SolanaRpc,
  programId: string,
  mint: string | null,
): Promise<SolanaTokenConfig> {
  const addresses = solanaPoolAddresses(programId);
  const { accounts } = await getSolanaAccounts(
    rpc,
    [addresses.tokenConfig(mint)],
    "finalized",
  );
  const info = accounts[0];
  if (!info) fail("this token is not configured on the pool");
  if (info.owner !== addresses.programId) {
    fail("token config account is not owned by the pool program");
  }
  return decodeSolanaTokenConfig(info.data);
}

function coordinate(value: unknown, label: string): string {
  if (
    typeof value !== "string" ||
    !/^(0x[0-9a-fA-F]{1,64}|[0-9]{1,78})$/.test(value)
  ) {
    fail(`${label} is not a field coordinate`);
  }
  return value;
}

function parseProofResponse(value: unknown): SolanaDepositProofResponse {
  const proof = value as Record<string, unknown> | null;
  if (!proof || typeof proof !== "object") fail("prover returned no proof");
  const a = proof.pi_a as unknown[];
  const b = proof.pi_b as unknown[][];
  const c = proof.pi_c as unknown[];
  if (!Array.isArray(a) || a.length < 2 || !Array.isArray(c) || c.length < 2) {
    fail("proof.pi_a and proof.pi_c must be G1 points");
  }
  if (
    !Array.isArray(b) ||
    b.length < 2 ||
    !Array.isArray(b[0]) ||
    !Array.isArray(b[1])
  ) {
    fail("proof.pi_b must be a G2 point");
  }
  return {
    pi_a: [
      coordinate(a[0], "proof.pi_a[0]"),
      coordinate(a[1], "proof.pi_a[1]"),
    ],
    pi_b: [
      [
        coordinate(b[0][0], "proof.pi_b[0][0]"),
        coordinate(b[0][1], "proof.pi_b[0][1]"),
      ],
      [
        coordinate(b[1][0], "proof.pi_b[1][0]"),
        coordinate(b[1][1], "proof.pi_b[1][1]"),
      ],
    ],
    pi_c: [
      coordinate(c[0], "proof.pi_c[0]"),
      coordinate(c[1], "proof.pi_c[1]"),
    ],
  };
}

async function requestDepositProof(params: {
  recoveryDigest?: `0x${string}`;
  network: SolanaNetworkConfig;
  npk: `0x${string}`;
  tokenHash: `0x${string}`;
  amount: bigint;
  commitment: string;
  signal?: AbortSignal;
  onLog: SolanaDepositLog;
}): Promise<SolanaDepositProofResponse> {
  const payload = await proveDepositLocally(
    {
      solana: {
        cluster: params.network.cluster,
        programId: params.network.programId,
        allowDevelopmentSetup: params.network.allowDevelopmentSetup,
      },
      npk: params.npk,
      tokenHash: params.tokenHash,
      amount: params.amount.toString(),
      ...(params.recoveryDigest
        ? { recoveryDigest: params.recoveryDigest }
        : {}),
    },
    params.signal,
    (stage) =>
      params.onLog(
        stage === "loading"
          ? "Loading browser proving assets"
          : "Generating the deposit proof in your browser",
        "info",
      ),
  );
  const record = payload as unknown as Record<string, unknown>;
  if (!record || typeof record !== "object" || record.success !== true) {
    fail("local deposit prover did not report success");
  }
  const returned = String(record.commitment || "").toLowerCase();
  if (returned !== params.commitment.toLowerCase()) {
    fail("prover commitment does not match the wallet-owned note commitment");
  }
  if (
    typeof record.token_hash !== "string" ||
    record.token_hash.toLowerCase() !== params.tokenHash.toLowerCase() ||
    record.amount !== params.amount.toString()
  ) {
    fail("prover public inputs do not match the deposit");
  }
  if (
    params.recoveryDigest &&
    String(record.recovery_digest).toLowerCase() !==
      params.recoveryDigest.toLowerCase()
  )
    fail("prover recovery digest does not match the encrypted output");
  return parseProofResponse(record.proof);
}

/** The signed bytes must be our message with exactly the signer's signature in front. */
function assertSignedTransaction(
  signed: Uint8Array,
  message: SolanaCompiledMessage,
  signer: SolanaDepositSigner,
): void {
  const expectedLength =
    1 + 64 * message.header.numRequiredSignatures + message.bytes.length;
  if (
    signed.length !== expectedLength ||
    signed[0] !== message.header.numRequiredSignatures
  ) {
    fail(
      "wallet returned a transaction with a different shape than the one it was given",
    );
  }
  if (
    !bytesEqual(
      signed.subarray(expectedLength - message.bytes.length),
      message.bytes,
    )
  ) {
    fail("wallet altered the transaction message before signing");
  }
  const signature = signed.subarray(1, 65);
  if (!nacl.sign.detached.verify(message.bytes, signature, signer.publicKey)) {
    fail("wallet signature does not verify for the connected account");
  }
}

async function depositRecoveryInstructions(
  params: SolanaDepositParams,
  publication: SolanaRecoveryPublication | undefined,
  depositInstruction: SolanaInstruction,
): Promise<SolanaInstruction[]> {
  if (!params.network.onchainRecoveryEnabled) return [depositInstruction];
  if (!publication)
    fail("Deposit is missing its proof-bound recovery ciphertext");
  const publish = buildSolanaPublishRecoveryRecordInstruction({
    programId: params.network.programId,
    payer: params.signer.address,
    ...publication,
  });
  const attached = [publish, depositInstruction];
  const probe = compileSolanaMessage({
    payer: params.signer.address,
    recentBlockhash: SOLANA_PLACEHOLDER_BLOCKHASH,
    instructions: [
      solanaComputeUnitLimitInstruction(MAX_COMPUTE_UNITS),
      solanaComputeUnitPriceInstruction(COMPUTE_UNIT_PRICE_MICRO_LAMPORTS),
      ...attached,
    ],
  });
  const byteLength =
    1 + 64 * probe.header.numRequiredSignatures + probe.bytes.length;
  if (byteLength <= SOLANA_PACKET_DATA_SIZE) return attached;
  // A large SPL merge may not fit the packet. Publish only ciphertext in a
  // separate wallet transaction. The protected deposit itself verifies the
  // immutable bytes and proof binding, so confirmed staging is sufficient.
  params.onLog(
    "Saving encrypted recovery data on Solana; confirm in your wallet",
    "info",
  );
  const blockhash = await getSolanaLatestBlockhash(params.rpc, "confirmed");
  const message = compileSolanaMessage({
    payer: params.signer.address,
    recentBlockhash: blockhash.blockhash,
    instructions: [publish],
  });
  const signed = await params.signer.signTransaction(
    solanaTransactionBytes(message),
  );
  assertSignedTransaction(signed, message, params.signer);
  const signature = await sendSolanaTransaction(params.rpc, signed);
  await confirmSolanaSignature(params.rpc, {
    signature,
    commitment: "confirmed",
    signal: params.signal,
  });
  return [depositInstruction];
}

async function executePreparedSolanaDepositMerge(
  params: SolanaDepositParams,
  prepared: SolanaDepositMergePrepared,
): Promise<SolanaDepositResult> {
  const { rpc, network, signer, onLog } = params;
  const addresses = solanaPoolAddresses(network.programId);
  const recoveryInstructions = await depositRecoveryInstructions(
    params,
    prepared.recoveryPublication,
    prepared.instruction,
  );
  const journalId = await solanaSettlementJournalId(
    network.key,
    addresses.programId,
    [prepared.sourceNullifier],
  );
  const blockhash = await getSolanaLatestBlockhash(rpc, "confirmed");
  const { record, resumed } = await prepareSolanaJournalRecord(
    params.journalScope,
    {
      id: journalId,
      kind: "deposit_merge",
      deposit: {
        commitment: prepared.note.commitment,
        mint: params.mint,
        amount: params.amount.toString(),
        treeIndex: prepared.note.treeIndex!,
        transactionWindow: {
          blockhash: blockhash.blockhash,
          lastValidBlockHeight: blockhash.lastValidBlockHeight,
          slot: blockhash.slot,
        },
        merge: {
          sourceNullifier: prepared.sourceNullifier,
          sourceCommitment: prepared.input.note.commitment,
          merkleRoot: prepared.merkleRoot,
          deadlineSlot: prepared.deadlineSlot.toString(),
          nonce: prepared.nonce,
        },
      },
      sourceCommitments: [prepared.input.note.commitment],
      sourceNullifiers: [prepared.sourceNullifier],
      outputNotes: [prepared.note],
      deliveries: [
        {
          commitment: prepared.note.commitment,
          recipientMpkHex: flatFieldHex(params.spendingKeys.mpk),
          keepLocal: true,
        },
      ],
    },
  );
  if (resumed) {
    fail(
      "an atomic deposit for this private note is already in flight; sync before retrying",
    );
  }
  try {
    // The signed transaction atomically publishes recovery data, or the exact
    // immutable record was already verified at finality above.
    if (!network.onchainRecoveryEnabled) {
      if (!params.backupJournal)
        fail("encrypted Solana recovery backup is required before submission");
      await params.backupJournal(record);
    }
  } catch (error) {
    await updateSolanaJournalRecord(
      params.journalScope,
      record.id,
      {
        state: "failed",
        failureConfirmed: true,
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }

  let signed!: Uint8Array;
  try {
    assertNotAborted(params.signal, "after funded-merge recovery backup");
    onLog("Simulating the atomic deposit", "info");
    const probe = compileSolanaMessage({
      payer: signer.address,
      instructions: [
        solanaComputeUnitLimitInstruction(MAX_COMPUTE_UNITS),
        solanaComputeUnitPriceInstruction(COMPUTE_UNIT_PRICE_MICRO_LAMPORTS),
        ...recoveryInstructions,
      ],
      recentBlockhash: SOLANA_PLACEHOLDER_BLOCKHASH,
    });
    const simulation = await simulateSolanaMessage(rpc, probe, "confirmed");
    assertSolanaSimulationSucceeded(simulation);
    const consumed = BigInt(simulation.unitsConsumed ?? MAX_COMPUTE_UNITS);
    const estimated = (consumed * SIMULATION_HEADROOM_BPS) / 10_000n + 20_000n;
    const computeUnits = Number(
      estimated < BigInt(MAX_COMPUTE_UNITS)
        ? estimated
        : BigInt(MAX_COMPUTE_UNITS),
    );

    const message = compileSolanaMessage({
      payer: signer.address,
      instructions: [
        solanaComputeUnitLimitInstruction(computeUnits),
        solanaComputeUnitPriceInstruction(COMPUTE_UNIT_PRICE_MICRO_LAMPORTS),
        ...recoveryInstructions,
      ],
      recentBlockhash: blockhash.blockhash,
    });
    assertNotAborted(params.signal, "before funded-merge wallet signature");
    onLog("Requesting wallet signature", "info");
    signed = await signer.signTransaction(solanaTransactionBytes(message));
    assertSignedTransaction(signed, message, signer);
  } catch (error) {
    await updateSolanaJournalRecord(
      params.journalScope,
      record.id,
      {
        state: "failed",
        failureConfirmed: true,
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }

  const signature = ethers.encodeBase58(signed.subarray(1, 65));
  const broadcastRecord = await updateSolanaJournalRecord(
    params.journalScope,
    record.id,
    {
      state: "broadcast",
      signature,
      lastValidBlockHeight: blockhash.lastValidBlockHeight,
    },
    record,
  );
  if (network.onchainRecoveryEnabled)
    void params.backupJournal?.(broadcastRecord).catch(() => undefined);
  else await params.backupJournal!(broadcastRecord);
  try {
    const returnedSignature = await sendSolanaTransaction(rpc, signed, {
      preflightCommitment: "confirmed",
    });
    if (returnedSignature !== signature) {
      fail("RPC returned a different funded-deposit signature");
    }
  } catch (error) {
    await updateSolanaJournalRecord(
      params.journalScope,
      record.id,
      {
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }
  onLog("Atomic deposit submitted; waiting for finality", "info", signature);

  try {
    await confirmSolanaSignature(rpc, {
      signature,
      lastValidBlockHeight: blockhash.lastValidBlockHeight,
      commitment: "finalized",
      pollMs: params.pollMs,
      signal: params.signal,
    });
  } catch (error) {
    if (error instanceof SolanaTransactionExpiredError) {
      await updateSolanaJournalRecord(
        params.journalScope,
        record.id,
        { state: "expired", error: error.message },
        record,
      );
    } else if (!params.signal?.aborted) {
      await updateSolanaJournalRecord(
        params.journalScope,
        record.id,
        {
          ...(error instanceof SolanaTransactionFailedError
            ? { state: "failed" as const, failureConfirmed: true }
            : {}),
          error: error instanceof Error ? error.message : String(error),
        },
        record,
      );
    }
    throw error;
  }

  const finalized = await finalizeSolanaDepositMerge({
    rpc,
    network,
    signature,
    prepared,
    mint: params.mint,
    netCredit: params.amount,
  });
  await updateSolanaJournalRecord(
    params.journalScope,
    record.id,
    {
      state: "finalized",
      slot: finalized.slot,
      outputNotes: [finalized.note],
    },
    record,
  );
  onLog(
    "Atomic deposit-merge completed: spent 1 old note and created 1 replacement in this deposit",
    "success",
    signature,
  );
  return {
    signature,
    slot: finalized.slot,
    note: finalized.note,
    journalId: record.id,
    spentNote: prepared.input.note,
  };
}

export async function executeSolanaDeposit(
  params: SolanaDepositParams,
): Promise<SolanaDepositResult> {
  const { rpc, network, signer, onLog } = params;
  if (!network.enabled) fail(`${network.label} is not configured`);
  if (params.amount <= 0n || params.amount >= 1n << 64n) {
    fail("deposit amount must fit the nonzero uint64 range");
  }
  assertNotAborted(params.signal, "before deposit preparation");
  if (!Array.isArray(params.notes))
    fail("deposit requires completed note recovery");
  const recipientMpk = params.recipientMpk ?? params.spendingKeys.mpk;
  const selfDeposit = recipientMpk === params.spendingKeys.mpk;
  const recipientInbox = solanaRecipientInboxKey(
    params.recipientInboxPublicKeyHex,
  );
  if (!selfDeposit && !recipientInbox)
    fail(
      "a recipient deposit requires a valid inbox public key before submission",
    );
  const active = await activeSolanaJournalRecords(params.journalScope);
  const pending = active.filter(
    (record) =>
      ["prepared", "broadcast", "failed"].includes(record.state) &&
      (record.kind !== "deposit" ||
        record.deliveries.some((delivery) => delivery.keepLocal)),
  );
  const reserved = new Set(
    pending.flatMap((record) =>
      record.sourceCommitments.map((word) => word.toLowerCase()),
    ),
  );
  const freeNote = params.notes.some(
    ({ note, spent }) =>
      !spent &&
      note.leafIndex >= 0 &&
      solanaNoteMint(note) === params.mint &&
      !reserved.has(note.commitment.toLowerCase()),
  );
  if (
    selfDeposit &&
    pending.some(
      (record) =>
        (record.deposit?.mint === params.mint ||
          record.intent?.mint === params.mint ||
          params.notes.some(
            ({ note }) =>
              solanaNoteMint(note) === params.mint &&
              record.sourceCommitments.includes(note.commitment),
          )) &&
        (record.kind === "deposit" || !freeNote),
    )
  ) {
    fail(
      "a previous transaction is still pending; refresh recovery before retrying the deposit",
    );
  }
  // The deployment's recovery mode selects the pinned deposit verifier.
  // A recovery digest is not available until the note is constructed below.
  // The actual digest and matching verification key are checked again by
  // proveDepositLocally and its isolated WASM worker before signing.
  assertSolanaDepositProvingSetup({
    cluster: network.cluster,
    programId: network.programId,
    allowDevelopmentSetup: network.allowDevelopmentSetup,
    onchainRecoveryEnabled: network.onchainRecoveryEnabled === true,
  });

  onLog("Reading the pool state", "info");
  const {
    slot: poolSlot,
    poolConfig,
    addresses,
  } = await readSolanaPoolConfig(rpc, network);
  assertSolanaPoolServable(network, poolConfig);
  if (poolConfig.unsafeTestSetupKeys || !poolConfig.developmentSetup) {
    fail("pool verifying key setup differs from the pinned deposit artifacts");
  }
  const tokenConfig = await readSolanaTokenConfig(
    rpc,
    addresses.programId,
    params.mint,
  );
  assertSolanaRegisteredToken(network, params.mint, tokenConfig);
  if (!tokenConfig.enabled)
    fail("deposits of this token are disabled on the pool");
  const treeIndex = poolConfig.currentTreeIndex;
  // Native SOL has no token program on its config (the system program's zero
  // key stands in); notes and instructions use the SPL Token program for it.
  const tokenProgram =
    params.mint === null ? SOLANA_TOKEN_PROGRAM_ID : tokenConfig.tokenProgram;
  const tokenHash = computeSolanaTokenHash({
    cluster: network.cluster,
    programId: addresses.programId,
    poolConfig: addresses.poolConfig,
    treeIndex,
    mint: params.mint,
  });

  const [solBalance, fees] = await Promise.all([
    getSolanaBalance(rpc, signer.address, "confirmed"),
    estimateSolanaDepositFees(rpc, signer.address, {
      onchainRecovery: network.onchainRecoveryEnabled,
    }),
  ]);
  if (solBalance < fees.reserve + (params.mint === null ? params.amount : 0n)) {
    fail("wallet SOL balance does not cover the deposit plus fees and rent");
  }
  if (params.mint !== null) {
    const balance = await getSolanaTokenBalance(
      rpc,
      signer.address,
      params.mint,
      tokenConfig.tokenProgram,
      "confirmed",
    );
    if (balance < params.amount)
      fail("wallet token balance does not cover the deposit");
  }

  if (selfDeposit && params.notes.length > 0) {
    const recipientMpk = params.recipientMpk ?? params.spendingKeys.mpk;
    const merge = await prepareSolanaDepositMerge({
      rpc,
      network,
      payer: signer.address,
      mint: params.mint,
      tokenProgram,
      netCredit: params.amount,
      senderMpk: params.spendingKeys.mpk,
      recipientMpk,
      spendingKeys: params.spendingKeys,
      notes: params.notes,
      cache: params.cache,
      journalScope: params.journalScope,
      currentTreeIndex: treeIndex,
      currentSlot: poolSlot,
      tokenHash: tokenHash as `0x${string}`,
      privateHistory: params.privateHistory,
      signal: params.signal,
      onProgress: (phase) =>
        onLog(
          phase === "loading"
            ? "Atomic deposit-merge started: combining the existing note with this deposit"
            : phase === "proving"
              ? "Generating the atomic deposit proof in your browser"
              : "Checking the atomic deposit proof",
          "info",
        ),
    });
    if (merge) {
      const mergeFees = await estimateSolanaDepositFees(rpc, signer.address, {
        onchainRecovery: network.onchainRecoveryEnabled,
        depositMerge: true,
      });
      if (
        solBalance <
        mergeFees.reserve + (params.mint === null ? params.amount : 0n)
      ) {
        fail(
          "wallet SOL balance does not cover the atomic deposit plus fees and rent; select Max again",
        );
      }
      return executePreparedSolanaDepositMerge(params, merge);
    }
  }

  const note: JoinSplitNote = {
    ...(await createSolanaFlatNote({
      recipientMpk: params.recipientMpk ?? params.spendingKeys.mpk,
      network,
      treeIndex,
      mint: params.mint,
      tokenProgram,
      tokenHash,
      value: params.amount,
    })),
    ...(params.privateHistory ? { privateHistory: params.privateHistory } : {}),
  };

  onLog("Preparing the browser deposit prover", "info");
  const recoveryPublication = network.onchainRecoveryEnabled
    ? await prepareSolanaNoteRecovery(
        network,
        note,
        recipientInbox ??
          formatInboxPublicKeyHex(params.spendingKeys.spendingKey),
      )
    : undefined;
  const recoveryRecords: SolanaRecoveryRecordInput[] | undefined =
    recoveryPublication
      ? [
          {
            kind: 1,
            locator: recoveryPublication.locator,
            payload: ethers.hexlify(
              recoveryPublication.payload,
            ) as `0x${string}`,
          },
        ]
      : undefined;
  const recoveryDigest = recoveryRecords
    ? computeSolanaRecoveryRecordsDigest({
        cluster: network.cluster,
        programId: network.programId,
        commitments: [note.commitment as `0x${string}`],
        records: recoveryRecords,
      })
    : undefined;
  const proof = await requestDepositProof({
    recoveryDigest,
    network,
    npk: flatFieldHex(note.npk),
    tokenHash: tokenHash as `0x${string}`,
    amount: params.amount,
    commitment: note.commitment,
    signal: params.signal,
    onLog,
  });
  assertNotAborted(params.signal, "after proving");

  const deposit = buildSolanaDeposit({
    recoveryRecords,
    cluster: network.cluster,
    programId: addresses.programId,
    depositor: signer.address,
    mint: params.mint,
    tokenProgram,
    currentTreeIndex: treeIndex,
    commitment: note.commitment as `0x${string}`,
    amount: params.amount,
    proof,
  });
  if (deposit.tokenHash.toLowerCase() !== tokenHash.toLowerCase()) {
    fail("SDK deposit token domain differs from the pool's");
  }
  const recoveryInstructions = await depositRecoveryInstructions(
    params,
    recoveryPublication,
    deposit.instruction,
  );

  const journalId = await solanaDepositJournalId(
    network.key,
    addresses.programId,
    note.commitment,
  );
  const blockhash = await getSolanaLatestBlockhash(rpc, "confirmed");
  const { record } = await prepareSolanaJournalRecord(params.journalScope, {
    id: journalId,
    kind: "deposit",
    deposit: {
      commitment: note.commitment,
      mint: params.mint,
      amount: params.amount.toString(),
      treeIndex,
      transactionWindow: {
        blockhash: blockhash.blockhash,
        lastValidBlockHeight: blockhash.lastValidBlockHeight,
        slot: blockhash.slot,
      },
    },
    sourceCommitments: [],
    sourceNullifiers: [],
    outputNotes: [note],
    deliveries: [
      {
        commitment: note.commitment,
        recipientMpkHex: flatFieldHex(recipientMpk),
        ...(recipientInbox
          ? { recipientInboxPublicKeyHex: recipientInbox }
          : {}),
        keepLocal: selfDeposit,
      },
    ],
  });
  try {
    // Recovery is atomically published with this signed deposit.
    if (!network.onchainRecoveryEnabled) {
      if (!params.backupJournal)
        fail("encrypted Solana recovery backup is required before submission");
      await params.backupJournal(record);
    }
  } catch (error) {
    // The wallet was never asked to sign, so this deposit cannot have landed.
    await updateSolanaJournalRecord(
      params.journalScope,
      record.id,
      {
        state: "failed",
        failureConfirmed: true,
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }
  let signed!: Uint8Array;
  try {
    assertNotAborted(params.signal, "after recovery backup");
    onLog("Simulating the deposit", "info");
    const probe = compileSolanaMessage({
      payer: signer.address,
      instructions: [
        solanaComputeUnitLimitInstruction(MAX_COMPUTE_UNITS),
        solanaComputeUnitPriceInstruction(COMPUTE_UNIT_PRICE_MICRO_LAMPORTS),
        ...recoveryInstructions,
      ],
      recentBlockhash: SOLANA_PLACEHOLDER_BLOCKHASH,
    });
    const simulation = await simulateSolanaMessage(rpc, probe, "confirmed");
    assertSolanaSimulationSucceeded(simulation);
    const consumed = BigInt(simulation.unitsConsumed ?? MAX_COMPUTE_UNITS);
    const computeUnits = Number(
      (consumed * SIMULATION_HEADROOM_BPS) / 10_000n + 20_000n <
        BigInt(MAX_COMPUTE_UNITS)
        ? (consumed * SIMULATION_HEADROOM_BPS) / 10_000n + 20_000n
        : BigInt(MAX_COMPUTE_UNITS),
    );

    const message = compileSolanaMessage({
      payer: signer.address,
      instructions: [
        solanaComputeUnitLimitInstruction(computeUnits),
        solanaComputeUnitPriceInstruction(COMPUTE_UNIT_PRICE_MICRO_LAMPORTS),
        ...recoveryInstructions,
      ],
      recentBlockhash: blockhash.blockhash,
    });

    assertNotAborted(params.signal, "before wallet signature");

    onLog("Requesting wallet signature", "info");
    signed = await signer.signTransaction(solanaTransactionBytes(message));
    assertSignedTransaction(signed, message, signer);
  } catch (error) {
    // No signed bytes were accepted by the RPC. A rejected simulation or
    // wallet signature cannot consume this commitment on chain.
    await updateSolanaJournalRecord(
      params.journalScope,
      record.id,
      {
        state: "failed",
        failureConfirmed: true,
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }

  // Persist the locally verified signature before the network can accept it.
  const signature = ethers.encodeBase58(signed.subarray(1, 65));
  const broadcastRecord = await updateSolanaJournalRecord(
    params.journalScope,
    record.id,
    {
      state: "broadcast",
      signature,
      lastValidBlockHeight: blockhash.lastValidBlockHeight,
    },
    record,
  );
  if (network.onchainRecoveryEnabled)
    void params.backupJournal?.(broadcastRecord).catch(() => undefined);
  else await params.backupJournal!(broadcastRecord);
  try {
    const returnedSignature = await sendSolanaTransaction(rpc, signed, {
      preflightCommitment: "confirmed",
    });
    if (returnedSignature !== signature)
      fail("RPC returned a different deposit signature");
  } catch (error) {
    await updateSolanaJournalRecord(
      params.journalScope,
      record.id,
      {
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }
  onLog("Deposit submitted; waiting for finality", "info", signature);

  try {
    await confirmSolanaSignature(rpc, {
      signature,
      lastValidBlockHeight: blockhash.lastValidBlockHeight,
      commitment: "finalized",
      pollMs: params.pollMs,
      signal: params.signal,
    });
  } catch (error) {
    if (error instanceof SolanaTransactionExpiredError) {
      await updateSolanaJournalRecord(
        params.journalScope,
        record.id,
        {
          state: "expired",
          error: error.message,
        },
        record,
      );
    } else if (!params.signal?.aborted) {
      await updateSolanaJournalRecord(
        params.journalScope,
        record.id,
        {
          ...(error instanceof SolanaTransactionFailedError
            ? { state: "failed" as const, failureConfirmed: true }
            : {}),
          error: error instanceof Error ? error.message : String(error),
        },
        record,
      );
    }
    throw error;
  }

  const finalized = await finalizeSolanaDeposit(rpc, network, {
    signature,
    note,
    amount: params.amount,
  });
  await updateSolanaJournalRecord(
    params.journalScope,
    record.id,
    {
      state: "finalized",
      slot: finalized.slot,
      outputNotes: [finalized.note],
    },
    record,
  );
  onLog("Deposit finalized", "success", signature);
  return {
    signature,
    slot: finalized.slot,
    note: finalized.note,
    journalId: record.id,
  };
}
